import React, { useState, useEffect } from "react";
import api from "../api";
import { toast } from "sonner";
import { Link2, Copy, Trash2, Plus, Eye, EyeOff, Lock, Download, Heart } from "lucide-react";

const ShareLinkManager = ({ projectId }) => {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    password: "",
    allow_downloads: true,
    allow_selection: true,
    expires_days: 0,
  });

  useEffect(() => {
    loadShares();
  }, [projectId]);

  const loadShares = async () => {
    try {
      const res = await api.get(`/projects/${projectId}/shares`);
      setShares(res.data);
    } catch {
      toast.error("Blad ladowania linkow");
    } finally {
      setLoading(false);
    }
  };

  const createShare = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        allow_downloads: form.allow_downloads,
        allow_selection: form.allow_selection,
      };
      if (form.password) payload.password = form.password;
      if (form.expires_days > 0) {
        const d = new Date();
        d.setDate(d.getDate() + parseInt(form.expires_days));
        payload.expires_at = d.toISOString();
      }
      await api.post(`/projects/${projectId}/share`, payload);
      setShowCreate(false);
      setForm({ password: "", allow_downloads: true, allow_selection: true, expires_days: 0 });
      loadShares();
      toast.success("Link udostepniania utworzony");
    } catch {
      toast.error("Blad tworzenia linka");
    }
  };

  const toggleActive = async (share) => {
    try {
      await api.put(`/projects/${projectId}/shares/${share.id}`, { active: !share.active });
      loadShares();
    } catch {
      toast.error("Blad aktualizacji");
    }
  };

  const deleteShare = async (shareId) => {
    if (!window.confirm("Usunac ten link?")) return;
    try {
      await api.delete(`/projects/${projectId}/shares/${shareId}`);
      loadShares();
      toast.success("Link usuniety");
    } catch {
      toast.error("Blad usuwania");
    }
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/g/${token}`;
    navigator.clipboard.writeText(url);
    toast.success("Link skopiowany do schowka");
  };

  if (loading) return <div className="text-center py-8 text-slate-500">Ladowanie...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-900">Linki udostepniania</h3>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1 transition-colors"
        >
          <Plus className="w-4 h-4" /> Nowy link
        </button>
      </div>

      {/* Create form */}
      {showCreate && (
        <form onSubmit={createShare} className="bg-slate-50 rounded-lg p-4 space-y-3 border border-slate-200">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Haslo (opcjonalne)</label>
            <input
              type="text"
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none"
              placeholder="Pozostaw puste = bez hasla"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Wygasa po (dni, 0 = nigdy)</label>
            <input
              type="number"
              value={form.expires_days}
              onChange={(e) => setForm((f) => ({ ...f, expires_days: e.target.value }))}
              className="w-32 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none"
              min="0"
            />
          </div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.allow_downloads}
                onChange={(e) => setForm((f) => ({ ...f, allow_downloads: e.target.checked }))}
                className="rounded border-slate-300"
              />
              Pozwol pobierac
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.allow_selection}
                onChange={(e) => setForm((f) => ({ ...f, allow_selection: e.target.checked }))}
                className="rounded border-slate-300"
              />
              Pozwol wybierac
            </label>
          </div>
          <div className="flex gap-2">
            <button type="submit" className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700">
              Utworz link
            </button>
            <button type="button" onClick={() => setShowCreate(false)} className="text-slate-600 px-4 py-2 rounded-lg text-sm hover:bg-slate-100">
              Anuluj
            </button>
          </div>
        </form>
      )}

      {/* Share links list */}
      {shares.length === 0 ? (
        <div className="text-center py-8 text-slate-400">
          <Link2 className="w-8 h-8 mx-auto mb-2" />
          <p>Brak linkow udostepniania</p>
        </div>
      ) : (
        <div className="space-y-2">
          {shares.map((share) => (
            <div
              key={share.id}
              className={`bg-white rounded-lg border p-4 flex items-center justify-between ${
                share.active ? "border-slate-200" : "border-slate-100 opacity-60"
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <code className="text-sm bg-slate-100 px-2 py-0.5 rounded truncate max-w-xs">
                    /g/{share.token}
                  </code>
                  {share.password && <Lock className="w-3.5 h-3.5 text-amber-500" title="Chroniony haslem" />}
                  {share.allow_downloads && <Download className="w-3.5 h-3.5 text-green-500" title="Pobieranie" />}
                  {share.allow_selection && <Heart className="w-3.5 h-3.5 text-pink-500" title="Selekcja" />}
                </div>
                <p className="text-xs text-slate-500">
                  Utworzono: {new Date(share.created_at).toLocaleDateString("pl-PL")}
                  {share.expires_at && ` | Wygasa: ${new Date(share.expires_at).toLocaleDateString("pl-PL")}`}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => copyLink(share.token)} className="p-2 hover:bg-slate-100 rounded-lg" title="Kopiuj link">
                  <Copy className="w-4 h-4 text-slate-500" />
                </button>
                <button onClick={() => toggleActive(share)} className="p-2 hover:bg-slate-100 rounded-lg" title={share.active ? "Dezaktywuj" : "Aktywuj"}>
                  {share.active ? <Eye className="w-4 h-4 text-green-500" /> : <EyeOff className="w-4 h-4 text-slate-400" />}
                </button>
                <button onClick={() => deleteShare(share.id)} className="p-2 hover:bg-red-50 rounded-lg" title="Usun">
                  <Trash2 className="w-4 h-4 text-red-400" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ShareLinkManager;
